import PropTypes from 'prop-types';
import React from 'react';
import styled, { useTheme } from 'styled-components';
import { Icon, PlainButton, tokens } from 'components/v3';
import FormsSelect from '../../components/FormsSelect';
import { brand } from '../../shared/brand';
import { typeLabel } from '../../shared/fieldTypes';
import {
  checkMapping,
  compatibleSources,
  guessSource,
  PREFILL_GROUPS,
  PREFILL_SOURCES,
  prefillFit,
  sourceLabel,
} from '../../shared/prefillSources';
import ToggleRow from '../fields/ToggleRow';
import { Divider, Hint, SectionLabel } from './parts';

// "Pre-fill from <Brand>" (plan §2.4, design: MappingPicker). Hidden when
// the type has no compatible source. Turning it on picks the label's
// guess (else the first natural fit); "Maps to" lists every source,
// grouped, with the incompatible ones disabled; the live check explains
// the choice and can suggest a better one.

const tone = (rgb, icon) => ({
  color: `rgb(${rgb})`,
  border: `rgba(${rgb}, 0.4)`,
  background: `rgba(${rgb}, 0.1)`,
  icon,
});

const TONES = {
  ok: tone('34, 197, 94', 'circle-check'),
  warn: tone('245, 158, 11', 'triangle-alert'),
  blocked: tone('237, 67, 55', 'triangle-alert'),
};

const LinkButton = styled(PlainButton)`
  display: inline;
  margin-left: 6px;
  font-size: 12px;
  font-weight: 700;
  color: ${(p) => p.$color};
  text-decoration: underline;
`;

const optionText = (source, fieldType) => {
  const fit = prefillFit(source.key, fieldType);
  if (!fit) return `${source.label} — not for ${typeLabel(fieldType)} fields`;
  return fit === 'warn' ? `${source.label} — works, see note` : source.label;
};

// "Maps to" options: every source, grouped, the incompatible ones disabled.
export const sourceOptions = (fieldType) =>
  PREFILL_GROUPS.map((group) => ({
    label: group,
    options: PREFILL_SOURCES.filter((s) => s.group === group).map((s) => ({
      label: optionText(s, fieldType),
      value: s.key,
      isDisabled: !prefillFit(s.key, fieldType),
    })),
  }));

const CheckNote = ({ check, onUse }) => {
  const t = tokens(useTheme());
  const style = TONES[check.level];
  return (
    <div
      style={{
        display: 'flex',
        gap: 8,
        alignItems: 'flex-start',
        margin: '-4px 0 8px',
        padding: '9px 11px',
        borderRadius: 8,
        fontSize: 12,
        lineHeight: 1.55,
        color: t.sub,
        border: `1px solid ${style.border}`,
        background: style.background,
      }}
    >
      <Icon name={style.icon} size={15} color={style.color} />
      <span>
        {check.message}
        {check.suggest ? (
          <LinkButton onClick={() => onUse(check.suggest)} $color={t.primary}>
            {`Use ${sourceLabel(check.suggest)} instead`}
          </LinkButton>
        ) : null}
      </span>
    </div>
  );
};

CheckNote.propTypes = {
  check: PropTypes.shape({
    level: PropTypes.string.isRequired,
    message: PropTypes.string,
    suggest: PropTypes.string,
  }).isRequired,
  onUse: PropTypes.func.isRequired,
};

const PrefillSection = ({ field, onChange, disabled }) => {
  if (!compatibleSources(field.type).length) return null;
  const appName = brand().name;
  const on = !!field.map;
  const check = on ? checkMapping(field) : null;
  const selectId = `fb-map-${field.id}`;
  return (
    <>
      <Divider />
      <SectionLabel>{`Pre-fill from ${appName}`}</SectionLabel>
      <ToggleRow
        first
        label="Mappable field"
        sub={`Starts filled in from the person’s ${appName} data`}
        on={on}
        onChange={(next) =>
          onChange({ map: next ? guessSource(field.label, field.type) : null })
        }
      />
      {on ? (
        <div style={{ margin: '6px 0 14px' }}>
          <FormsSelect
            id={selectId}
            label="Maps to"
            value={field.map}
            options={sourceOptions(field.type)}
            disabled={disabled}
            onChange={(key) => onChange({ map: key })}
          />
        </div>
      ) : null}
      {check ? (
        <CheckNote check={check} onUse={(key) => onChange({ map: key })} />
      ) : null}
      <Hint top={2}>
        {on
          ? `When someone opens this form in the app, this field starts filled with their ${sourceLabel(
              field.map
            ).toLowerCase()}. They can still change it before submitting.`
          : 'Turn on to pre-populate this field from their profile or account data when they fill the form in the app.'}
      </Hint>
    </>
  );
};

PrefillSection.propTypes = {
  field: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    label: PropTypes.string,
    map: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

PrefillSection.defaultProps = { disabled: false };

export default PrefillSection;
